// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import * as React from 'react';
import { useUserContext } from '../../context/UserContext';
import { Box, Collapse, IconButton, Typography } from '../../lib/base';
import { KeyboardArrowDownIcon, KeyboardArrowUpIcon } from '../../lib/icons';
import { formatTime, getID } from '../../utils';
import { type TrayChangeInfo, getChangeAction } from '../order/OrderInputsChangeTable';

export interface OrderContainerData {
  readonly id: string;
  readonly order_fulfillment_time: number;
  readonly extra?: string;
}

export function ChildOrdersContainer({
  ordersIn,
  title,
}: {
  ordersIn: OrderContainerData[];
  title: string;
}) {
  const { timezone } = useUserContext();
  const ordersWithFormattedTime = ordersIn.map((order) => ({
    ...order,
    formattedId: getID(order.id),
    formattedTime: formatTime(order.order_fulfillment_time, { timezone }),
  }));
  const maxIdLength = Math.max(...ordersWithFormattedTime.map((order) => order.formattedId.length));
  const maxFormattedTimeLength = Math.max(...ordersWithFormattedTime.map((order) => order.formattedTime.length));
  return (
    <>
      <Typography>{title}</Typography>
      <Box>
        <ul>
          {ordersWithFormattedTime.map((order) => (
            <li key={order.id}>
              <Typography>
                Order {order.formattedId.padEnd(maxIdLength, ' ')} -{' '}
                {order.formattedTime.padEnd(maxFormattedTimeLength, ' ')}
                {order.extra && order.extra}
              </Typography>
            </li>
          ))}
        </ul>
      </Box>
    </>
  );
}

interface OrderContainerWithChangesData {
  readonly order: OrderContainerData;
  readonly changes: readonly TrayChangeInfo[];
}

export function ChildOrdersContainerWithChanges({
  ordersIn,
  title,
}: {
  ordersIn: OrderContainerWithChangesData[];
  title: string;
}) {
  const { timezone } = useUserContext();
  const ordersWithFormattedTime = ordersIn.map((order) => ({
    ...order,
    formattedId: getID(order.order.id),
    formattedTime: formatTime(order.order.order_fulfillment_time, { timezone }),
  }));
  const maxIdLength = Math.max(...ordersWithFormattedTime.map((order) => order.formattedId.length));
  const maxFormattedTimeLength = Math.max(...ordersWithFormattedTime.map((order) => order.formattedTime.length));
  const ordersWithLabels = ordersWithFormattedTime.map((order) => ({
    ...order,
    label: `Order ${order.formattedId.padEnd(maxIdLength, ' ')} - ${order.formattedTime.padEnd(maxFormattedTimeLength, ' ')}`,
  }));

  return (
    <>
      <Typography>{title}</Typography>
      <Box>
        <ul>
          {ordersWithLabels.map((order) => (
            <li key={order.order.id}>
              <DropdownOrderContainer label={order.label} changes={order.changes} />
            </li>
          ))}
        </ul>
      </Box>
    </>
  );
}

function DropdownOrderContainer({
  label,
  changes,
}: {
  changes: readonly TrayChangeInfo[];
  label: string;
}) {
  const [open, setOpen] = React.useState(false);

  return (
    <>
      <Box display="flex" flexDirection="row" alignItems="center">
        <Typography>{label}</Typography>
        <IconButton aria-label="show-details" sx={{ ml: 1 }} onClick={() => setOpen(!open)}>
          {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>
      </Box>
      <Collapse
        in={open}
        unmountOnExit
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <ul>
          {changes.map((change) => {
            const cropName = change.cropName;
            const action = getChangeAction(change);

            return (
              <li key={cropName}>
                <Typography variant="body1">
                  {cropName} - {action}
                </Typography>
              </li>
            );
          })}
        </ul>
      </Collapse>
    </>
  );
}
